// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useAggregationData } from '@shared/advisory/aggregation/providers';
import { type ReturnsHeatMapValue } from '@shared/data-access/interfaces/common/returns';
import {
  formatToMoney,
  formatToPercent,
  type HistValue,
  type KpiBoxesProps,
} from '@shared/ui-kit';
import { useMemo } from 'react';
import { buildValueCreationHoldings } from '../../utils/build-value-creation';
import { usePeriodPicker } from '../../hooks/use-period-picker';
import { type Tag } from '@shared/data-access/interfaces/redux';
import { useTranslation } from 'react-i18next';

export const useReturns = ({ tags }: { tags?: Tag[] } = {}) => {
  const { t } = useTranslation(['glossary']);
  const {
    status,
    currency,
    institutionId,
    onRefresh,
    accountsPositions,
    hasHoldings,
  } = useAggregationData();

  const periodPicker = usePeriodPicker();

  const returnPeriod = useMemo(
    () =>
      new Map([
        ['1M', 'm1_return'] as const,
        ['3M', 'm3_return'] as const,
        ['6M', 'm6_return'] as const,
        ['YTD', 'ytd_return'] as const,
        ['1Y', 'y1_return'] as const,
      ]).get(
        periodPicker.defaultHist as Extract<
          HistValue,
          '1M' | '3M' | '6M' | 'YTD' | '1Y'
        >,
      ),
    [periodPicker.defaultHist],
  );

  const returns: ReturnsHeatMapValue[] = useMemo(
    () =>
      returnPeriod
        ? accountsPositions.holdings
            .filter(holding => !!holding.details)
            .map((holding, index) => ({
              id: `${
                holding.details?.ticker ??
                holding.details?.isin ??
                holding.ticker ??
                holding.isin
              }-${index}`,
              name: holding.details?.name ?? holding.name,
              ticker:
                holding.details?.ticker ??
                holding.details?.isin ??
                holding.ticker ??
                holding.isin ??
                '',
              weight: holding.weight,
              value: holding.details?.[returnPeriod] ?? 0,
              m1_return: holding.details!.m1_return,
              m3_return: holding.details!.m3_return,
              m6_return: holding.details!.m6_return,
              ytd_return: holding.details!.ytd_return,
              y1_return: holding.details!.y1_return,
              children: [],
            }))
        : [],
    [accountsPositions.holdings, returnPeriod],
  );

  const accountsValueCreation = useMemo(
    () =>
      buildValueCreationHoldings(
        accountsPositions.holdings,
        'account',
        returnPeriod,
      ).filter(value => value.initialValue !== 0),
    [accountsPositions.holdings, returnPeriod],
  );

  const assetClassValueCreation = useMemo(
    () =>
      buildValueCreationHoldings(
        accountsPositions.holdings,
        'assetClass',
        returnPeriod,
      ),
    [accountsPositions.holdings, returnPeriod],
  );

  const positionsValueCreation = useMemo(
    () =>
      buildValueCreationHoldings(
        accountsPositions.holdings,
        'positions',
        returnPeriod,
      ),
    [accountsPositions.holdings, returnPeriod],
  );

  const tagsValueCreation = useMemo(
    () =>
      buildValueCreationHoldings(
        accountsPositions.holdings,
        'categories',
        returnPeriod,
        tags,
      ),
    [accountsPositions.holdings, returnPeriod, tags],
  );

  /* istanbul ignore next */
  const kpis = useMemo<KpiBoxesProps[]>(() => {
    const investment = positionsValueCreation.reduce(
      (acc, val) => acc + val.finalValue,
      0,
    );

    const initialValue = positionsValueCreation.reduce(
      (acc, val) => acc + val.initialValue,
      0,
    );

    const valueCreated = positionsValueCreation.reduce(
      (acc, val) => acc + val.valueCreated,
      0,
    );

    return [
      {
        text: t('glossary.investment', { ns: 'glossary' }),
        value: formatToMoney(investment, currency, 0),
        dataTestid: 'kpi-investment',
        icon: 'TrendingUp',
        color: 'success',
      },
      {
        text: t('glossary.initial-value', { ns: 'glossary' }),
        value: formatToMoney(initialValue, currency),
        dataTestid: 'kpi-initial-value',
        icon: 'DollarSign',
        color: 'main',
      },
      {
        text: t('glossary.value-created', { ns: 'glossary' }),
        value: formatToMoney(valueCreated, currency),
        dataTestid: 'kpi-value-created',
        icon: 'Zap',
        color: 'success',
      },
      {
        text: t('glossary.return-irr', { ns: 'glossary' }),
        value: formatToPercent(
          initialValue === 0 ? 0 : valueCreated / initialValue,
          1,
        ),
        dataTestid: 'kpi-return',
        icon: 'Percent',
        color: 'main',
      },
    ];
  }, [currency, positionsValueCreation, t]);

  return {
    status,
    periodPicker,
    returns,
    accountsValueCreation,
    assetClassValueCreation,
    positionsValueCreation,
    tagsValueCreation,
    kpis,
    hasHoldings,
    returnPeriod,
    currency,
    institutionId,
    onRefresh,
  };
};
